import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SiteService } from '../config/site.service';
import { MessageService } from '../message/message.service';
import { Configuration } from '../models/configuration';
import { HttpRequestOptions } from '../models/http-request-options.model';

@Injectable({
    providedIn: 'root',
})
export class HttpClientService {

    protected configuration: Configuration;

    constructor(protected myHttpClient: HttpClient,
                protected mySiteService: SiteService,
                protected myMessageService: MessageService,
                @Optional() configuration: Configuration,
    ) {
        this.configuration = configuration;
    }

    /**
     * (usually) for SELECTs
     */
    public get(target: string, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.get(url, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    /**
     * (usually) for INSERTs
     */
    public post(target: string, body: any, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.post(url, body, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    /**
     * (usually) for UPDATEs
     */
    public put(target: string, body: any, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.put(url, body, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    /**
     * (usually) for DELETEs
     */
    public delete(target: string, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.delete(url, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    /**
     * (usually) for cache invalidation of SELECTs
     */
    public head(target: string, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.head(url, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    /**
     * (usually) for partial UPDATESs (rarely implemented!)
     */
    public patch(target: string, body: any, options?: HttpRequestOptions, skipDefaultErrorHandling?: boolean): Observable<any> {

        const url: string = this.getURL(target);
        const args: HttpRequestOptions = this.createArgs(options);

        return this.myHttpClient.patch(url, body, args).pipe(catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)));
    }

    // -----

    protected getURL(target: string): string {
        let basePath: { web: string, rest: string } = { web: null, rest: this.mySiteService.getConfigKey('rest') };
        if (this.configuration?.getBasePath) {
            basePath = this.configuration.getBasePath();
        }
        return basePath.rest + target;
    }

    protected createArgs(options?: HttpRequestOptions): HttpRequestOptions {
        if (options instanceof HttpRequestOptions) {
            return options;
        } else {
            const args: HttpRequestOptions = {
                params: this.createParamsArgs(options),
                headers: this.createHeaderArgs(options),
                withCredentials: true,
            };
            return args;
        }
    }

    protected createHeaderArgs(options?: HttpRequestOptions): HttpHeaders {
        let headers: HttpHeaders = new HttpHeaders();

        if (options?.headers) {
            (options.headers as HttpHeaders).keys().forEach((key: string) => {
                const value: string = (options.headers as HttpHeaders).get(key);
                headers = headers.append(key, value);
            });
        }

        return headers;
    }

    protected createParamsArgs(options?: HttpRequestOptions): HttpParams {
        let params: HttpParams = new HttpParams();

        if (options?.params) {
            const paramKeyArray: string[] = Object.keys(options.params);

            paramKeyArray.forEach((key: any) => {
                const paramValue: string = options.params[ key ];
                params = params.append(key, paramValue);
            });
        }

        return params;
    }

    protected handleError(error: HttpErrorResponse, skipDefaultErrorHandling?: boolean): Observable<never> {
        console.error(error);

        if (!skipDefaultErrorHandling) {
            this.myMessageService.displayError(error, 'sla-core.commons.error.unknown.text');
        }

        return throwError(error);
    }

}
